import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'

import { matchPath } from 'react-router-dom'

import { useLocation, useNavigate } from 'Pods/Shared/ContextBasedRouter'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import {
  GALLERY_IMAGE_PATH_PATTERN,
  IMAGE_PATH_PATTERN,
  STUDIO_PATH,
  buildImagePath,
  hasImageEditParam,
} from 'Pods/ThreeDStudio/constants'
import useApplyImageToThreeD from 'Pods/ThreeDStudio/Hooks/useApplyImageToThreeD'
import useRegenerateImageRun from 'Pods/ThreeDStudio/Hooks/useRegenerateImageRun'
import ImageLightbox from 'Pods/ThreeDStudio/Shared/ImageLightbox/ImageLightbox'
import { getAssetByTypeAndSource } from 'Pods/ThreeDStudio/Shared/Utils/getAssetByTypeAndSource'
import { openFullImageEditor, setActiveMobilePanel } from 'Pods/ThreeDStudio/Store/actions/ui'
import { MOBILE_PANEL } from 'Pods/ThreeDStudio/Store/constants'

const handleApplyNavigate = () => setActiveMobilePanel(MOBILE_PANEL.GENERATE)

// Opens the image a shared /ai-image/:slug URL names: in the lightbox, or in the full-width
// editor when the URL carries `edit=true`. /ai-image-generator/images/:slug?edit=true opens the
// editor too, while without `edit` the gallery shows that image in its own modal. An unknown or
// private slug never gets here, as ThreeDStudioController redirects it away. Mounted outside
// the routes, so it reads the slug off the pathname and renders nothing on any other page.
function ImageDeepLink() {
  const location = useLocation()
  const pathname = location?.pathname ?? ''
  const imageSlug = matchPath(IMAGE_PATH_PATTERN, pathname)?.params?.slug ?? null
  const gallerySlug = matchPath(GALLERY_IMAGE_PATH_PATTERN, pathname)?.params?.slug ?? null
  const isEditRequested = hasImageEditParam(location?.search)
  const slug = imageSlug ?? (isEditRequested ? gallerySlug : null)
  const navigate = useNavigate()
  const { getData } = useApiGet()
  const [loaded, setLoaded] = useState(null)

  useEffect(() => {
    if (!slug) return

    getData({
      path: `/api/internal/generator/images/${slug}`,
      onSuccess: (response) => {
        setLoaded({ slug, attributes: response?.data?.data?.attributes ?? null })
      },
    })
  }, [getData, slug])

  // Keyed on the slug so a stale image never shows while the next one loads.
  const attributes = loaded?.slug === slug ? loaded.attributes : null
  const { id: runId, outputAssets, title, prompt } = attributes || {}
  const imageTitle = title ?? prompt ?? ''
  const { assetId, fileUrl } = getAssetByTypeAndSource(outputAssets, 'image', 'ai_generated')

  const image = useMemo(
    () => (assetId && fileUrl ? { id: assetId, url: fileUrl, alt: imageTitle } : null),
    [assetId, fileUrl, imageTitle],
  )

  // No session id, as from the library card: the editor's first edit starts its own
  // session, which also keeps a visitor's edits out of the owner's session. A community image
  // also goes without its run id, as from the gallery's Edit, so the editor's Delete stays off
  // for another user's run.
  const editorRunId = gallerySlug ? null : runId

  useEffect(() => {
    if (isEditRequested && image) {
      openFullImageEditor({ image, runId: editorRunId })
    }
  }, [isEditRequested, image, editorRunId])

  const handleClose = useCallback(() => {
    navigate(STUDIO_PATH.IMAGE_GENERATOR)
  }, [navigate])

  // ImageLightbox closes before it edits, so this replaces the /ai-image entry the close
  // just pushed instead of stacking a second one.
  const handleEdit = useCallback(() => {
    navigate(buildImagePath(slug, { edit: true }), { replace: true })
  }, [navigate, slug])

  const applyImageToThreeD = useApplyImageToThreeD(handleApplyNavigate)

  const handleApply = useCallback(() => {
    applyImageToThreeD(image)
  }, [applyImageToThreeD, image])

  const { regenerateImage, isLoading: isRegenerating } = useRegenerateImageRun()

  const handleRegenerate = useCallback(() => {
    regenerateImage(runId)
  }, [regenerateImage, runId])

  if (!imageSlug || isEditRequested || !image) {
    return null
  }

  return (
    <ImageLightbox
      result={image}
      isGenerating={isRegenerating}
      commentsSlug={slug}
      onApply={handleApply}
      onRegenerate={handleRegenerate}
      onEdit={handleEdit}
      onClose={handleClose}
    />
  )
}

export default memo(ImageDeepLink)
